import * as THREE from 'three';
import { fbm, random } from '../look/noise.js';

/**
 * Textures made in code for part 2's worlds (29 Sep 2026, no downloads): a
 * leaf cluster for tree crowns, bark, brick, a flowering tuft, a curtain
 * wall, the sea floor's caustics. `DataTexture`s, like the grass blades
 * (grass.ts), so they build in Node and cost no download; each world records
 * them with `world.codeTexture` for the budget. Where a photographed CC0
 * texture is clearly better it replaced the code (part 2's downloads,
 * docs/superpowers/specs/2026-09-29-worlds-part2-downloads.md): the town
 * square's clinkers are gone from here, and its brick is a photo; the
 * polder's farm and the street's blocks keep this brick.
 */

/** An RGBA texture `width` × `height` from a function of the pixel (row 0 at the bottom). */
export function drawn(
  width: number,
  height: number,
  paint: (data: Uint8Array) => void,
  options: { srgb?: boolean; repeat?: boolean } = {},
): THREE.DataTexture {
  const data = new Uint8Array(width * height * 4);
  paint(data);
  const texture = new THREE.DataTexture(data, width, height);
  if (options.srgb !== false) texture.colorSpace = THREE.SRGBColorSpace;
  if (options.repeat !== false) {
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
  }
  texture.generateMipmaps = true;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.needsUpdate = true;
  return texture;
}

const byte = (v: number): number => Math.round(Math.min(1, Math.max(0, v)) * 255);

/** Fills an ellipse (centre, half-axes in pixels, turned `angle`) with a colour, alpha 255. */
function ellipse(
  data: Uint8Array,
  size: number,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  angle: number,
  colour: (u: number, v: number) => readonly [number, number, number],
): void {
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  const reach = Math.ceil(Math.max(rx, ry));
  for (let y = Math.floor(cy - reach); y <= Math.ceil(cy + reach); y++) {
    for (let x = Math.floor(cx - reach); x <= Math.ceil(cx + reach); x++) {
      if (x < 0 || y < 0 || x >= size || y >= size) continue;
      const dx = x - cx;
      const dy = y - cy;
      const u = (dx * c + dy * s) / rx;
      const v = (-dx * s + dy * c) / ry;
      if (u * u + v * v > 1) continue;
      const [r, g, b] = colour(u, v);
      data.set([byte(r), byte(g), byte(b), 255], (y * size + x) * 4);
    }
  }
}

/**
 * A round cluster of leaves on a clear ground, for a tree's crown: over a
 * hundred leaves in a disc, thicker in the middle, each a pointed oval with
 * a paler midrib. Nearly grey, so a crown's colour -- and its season -- is
 * the instance's own (trees.ts).
 */
export function leafCluster(size = 128, seed = 3): THREE.DataTexture {
  const next = random(seed);
  return drawn(
    size,
    size,
    (data) => {
      const leaves = Math.round(size * 0.9);
      for (let k = 0; k < leaves; k++) {
        const a = next() * Math.PI * 2;
        const r = Math.sqrt(next()) * size * 0.38;
        const cx = size / 2 + Math.cos(a) * r;
        const cy = size / 2 + Math.sin(a) * r;
        const long = size * (0.06 + 0.04 * next());
        const shade = 0.55 + 0.4 * next();
        const angle = next() * Math.PI;
        ellipse(data, size, cx, cy, long, long * 0.45, angle, (u, v) => {
          // Pointed at both ends, a paler vein down the middle, darker at the edge.
          const vein = Math.abs(v) < 0.12 ? 0.12 : 0;
          const edge = 1 - 0.25 * u * u;
          const l = shade * edge + vein;
          return [l * 0.93, l, l * 0.86];
        });
      }
    },
    { repeat: false },
  );
}

/** Bark: dark fissures running up a paler grey-brown, tiling round and up a trunk. */
export function bark(size = 128, seed = 5): THREE.DataTexture {
  const noise = fbm(seed, 4, 8);
  return drawn(size, size, (data) => {
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const u = x / size;
        const v = y / size;
        // Stretched up the trunk: ridges and fissures.
        const ridge = noise(u * 8, v * 1.5);
        const fissure = Math.pow(Math.abs(Math.sin((u * 10 + ridge * 2.2) * Math.PI)), 0.35);
        const l = 0.35 + 0.45 * fissure * (0.7 + 0.3 * noise(u * 16, v * 8));
        data.set([byte(l * 1.02), byte(l * 0.95), byte(l * 0.86), 255], (y * size + x) * 4);
      }
    }
  });
}

/**
 * Brick in stretcher bond, one metre square: 16 courses of 6.25 cm, bricks of
 * 22 cm with 1 cm joints, each brick a little lighter or darker. Pale and
 * nearly neutral, so a facade's own colour makes it red, brown or yellow.
 */
export function brick(size = 256, seed = 9): THREE.DataTexture {
  const next = random(seed);
  const shades = Array.from({ length: 16 * 5 }, () => 0.72 + 0.28 * next());
  const grain = fbm(seed + 1, 3, 16);
  return drawn(size, size, (data) => {
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const course = Math.floor((y / size) * 16);
        const along = (x / size) * 4.5 + (course % 2) * 0.5;
        const index = Math.floor(along);
        const inCourse = ((y / size) * 16) % 1;
        const inBrick = along % 1;
        const joint = inCourse < 0.16 || inBrick < 0.045;
        const shade = shades[(course * 5 + (index % 5)) % shades.length]!;
        const g = 0.9 + 0.1 * grain((x / size) * 16, (y / size) * 16);
        const [r, gr, b] = joint
          ? [0.86, 0.84, 0.8]
          : [shade * g, shade * 0.93 * g, shade * 0.86 * g];
        data.set([byte(r), byte(gr), byte(b), 255], (y * size + x) * 4);
      }
    }
  });
}

/**
 * A tuft of meadow grass in flower: blades as the island's tufts (grass.ts),
 * with a few flower heads at their tips in `flower`'s colour -- poppies,
 * daisies, buttercups -- on a clear ground.
 */
export function flowerTuft(
  flower: THREE.ColorRepresentation,
  centre: THREE.ColorRepresentation,
  size = 128,
  seed = 21,
): THREE.DataTexture {
  const next = random(seed);
  const petal = new THREE.Color(flower);
  const heart = new THREE.Color(centre);
  const root = new THREE.Color(0x3b5226);
  const tip = new THREE.Color(0x8fa458);
  const c = new THREE.Color();
  return drawn(
    size,
    size,
    (data) => {
      const tips: [number, number][] = [];
      const blades = Math.round(size / 5);
      for (let b = 0; b < blades; b++) {
        const x0 = size * (0.1 + 0.8 * next());
        const lean = (next() - 0.5) * size * 0.35;
        const tall = size * (0.45 + 0.45 * next());
        const width = size * (0.012 + 0.012 * next());
        for (let y = 0; y < tall; y++) {
          const t = y / tall;
          const cx = x0 + lean * t * t;
          const half = Math.max(0.35, width * (1 - t) ** 0.7);
          c.copy(root).lerp(tip, t);
          for (let x = Math.floor(cx - half); x <= Math.ceil(cx + half); x++) {
            if (x < 0 || x >= size) continue;
            data.set([byte(c.r), byte(c.g), byte(c.b), 255], (y * size + x) * 4);
          }
        }
        if (b % 3 === 0) tips.push([x0 + lean, tall]);
      }
      for (const [x, y] of tips.slice(0, 8)) {
        const r = size * (0.055 + 0.025 * next());
        ellipse(data, size, x, Math.min(size - r - 1, y), r, r * 0.8, 0, (u, v) => {
          const d = Math.hypot(u, v);
          const col = d < 0.35 ? heart : petal;
          const l = 0.85 + 0.15 * (1 - d);
          return [col.r * l, col.g * l, col.b * l];
        });
      }
    },
    { repeat: false },
  );
}

/**
 * A curtain wall of glass, 3 m wide and one storey (3.5 m) high: two panes
 * wide with slim mullions and a spandrel at each floor. The glass is dark
 * and even, so the sky's reflection (the material's) does the rest.
 */
export function curtainWall(width = 128, height = 128): THREE.DataTexture {
  return drawn(width, height, (data) => {
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const u = x / width;
        const v = y / height;
        const mullion = Math.min(Math.abs(u - 0.5), u, 1 - u) < 0.012;
        const spandrel = v < 0.2;
        const transom = Math.abs(v - 0.2) < 0.012 || v > 0.985;
        let rgb: [number, number, number];
        if (mullion || transom) rgb = [0.62, 0.64, 0.66];
        else if (spandrel) rgb = [0.28, 0.3, 0.33];
        else rgb = [0.16 + 0.05 * v, 0.2 + 0.05 * v, 0.25 + 0.06 * v];
        data.set([byte(rgb[0]), byte(rgb[1]), byte(rgb[2]), 255], (y * width + x) * 4);
      }
    }
  });
}

/**
 * The sea floor's caustics: the bright net that sunlight through waves casts
 * on the sand -- cells' edges (Worley's F2 − F1), thin and bright, tiling.
 */
export function caustics(size = 128, seed = 31): THREE.DataTexture {
  const next = random(seed);
  const cells = 6;
  const points = Array.from({ length: cells * cells }, (_, i) => [
    (i % cells) + next(),
    Math.floor(i / cells) + next(),
  ]);
  return drawn(
    size,
    size,
    (data) => {
      for (let y = 0; y < size; y++) {
        for (let x = 0; x < size; x++) {
          const u = (x / size) * cells;
          const v = (y / size) * cells;
          let f1 = 9;
          let f2 = 9;
          for (const [px, py] of points) {
            for (let ox = -1; ox <= 1; ox++) {
              for (let oy = -1; oy <= 1; oy++) {
                const d = Math.hypot(u - (px! + ox * cells), v - (py! + oy * cells));
                if (d < f1) {
                  f2 = f1;
                  f1 = d;
                } else if (d < f2) f2 = d;
              }
            }
          }
          const line = Math.pow(Math.max(0, 1 - (f2 - f1) * 3.2), 3);
          data.set([byte(line), byte(line), byte(line), 255], (y * size + x) * 4);
        }
      }
    },
    { srgb: false },
  );
}

/**
 * Roof tiles (pantiles) in rows, one metre square: rows of 33 cm, each tile a
 * wave across its width, a dark shadow line under each row's lap. Nearly
 * neutral: a roof's colour is its own (the town square's houses, part 2's
 * Quest check: "some roofs are not rendered great"). Rows run along u.
 */
export function roofTiles(size = 128, seed = 17): THREE.DataTexture {
  const next = random(seed);
  const rows = 3;
  const across = 4.5;
  const shades = Array.from({ length: 64 }, () => 0.82 + 0.18 * next());
  return drawn(size, size, (data) => {
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const v = (y / size) * rows;
        const row = Math.floor(v);
        const inRow = v - row;
        const u = (x / size) * across + (row % 2) * 0.5;
        const tile = Math.floor(u);
        const wave = 0.8 + 0.2 * Math.sin((u - tile) * Math.PI * 2);
        // The row above laps over this one: a shadow along its lower edge.
        const lap = inRow > 0.88 ? 0.55 : 1;
        const l = wave * lap * shades[(row * 7 + tile * 3) % shades.length]!;
        data.set([byte(l), byte(l * 0.97), byte(l * 0.94), 255], (y * size + x) * 4);
      }
    }
  });
}

/**
 * An oak floor for the villa (ADR-037): planks 20 cm wide and 0.9–2 m long in a staggered bond, one
 * repeat per 2 m of floor (ten planks across), each plank its own shade of warm oak with a fine
 * grain along it and a hairline joint round it. Coloured, not tinted.
 */
export function planks(size = 512, seed = 41): THREE.DataTexture {
  const next = random(seed);
  const rows = 10;
  // Where each row's joints fall across the 2 m (as a share of the repeat), and each plank's shade.
  const joints = Array.from({ length: rows }, () => {
    const cuts: number[] = [];
    let at = next() * 0.5;
    while (at < 1) {
      cuts.push(at);
      at += 0.45 + next() * 0.5;
    }
    return cuts;
  });
  const shades = Array.from({ length: rows * 4 }, () => 0.84 + 0.2 * next());
  const grain = fbm(seed + 1, 4, 8);
  return drawn(size, size, (data) => {
    for (let y = 0; y < size; y++) {
      const v = (y / size) * rows;
      const row = Math.floor(v);
      const across = v - row;
      const cuts = joints[row]!;
      for (let x = 0; x < size; x++) {
        const u = x / size;
        let plank = cuts.findIndex((cut) => cut > u);
        if (plank < 0) plank = cuts.length;
        const nearJoint = cuts.some((cut) => Math.abs(cut - u) < 0.0025);
        const edge = across < 0.03 || across > 0.97;
        const shade = shades[(row * 4 + plank) % shades.length]!;
        const fine = 0.9 + 0.1 * Math.sin((across * 9 + grain(u * 6, v) * 3) * Math.PI);
        const l = nearJoint || edge ? 0.55 : shade * fine;
        data.set([byte(0.74 * l), byte(0.56 * l), byte(0.38 * l), 255], (y * size + x) * 4);
      }
    }
  });
}

/**
 * The terrace's deck: boards 14 cm wide with dark gaps, weathered to a grey-brown, one repeat per
 * 1.4 m (ten boards). Coloured, not tinted.
 */
export function deck(size = 256, seed = 43): THREE.DataTexture {
  const next = random(seed);
  const boards = 10;
  const shades = Array.from({ length: boards }, () => 0.86 + 0.18 * next());
  const grain = fbm(seed + 1, 3, 8);
  return drawn(size, size, (data) => {
    for (let y = 0; y < size; y++) {
      const v = (y / size) * boards;
      const board = Math.floor(v);
      const across = v - board;
      for (let x = 0; x < size; x++) {
        const u = x / size;
        const gap = across > 0.93;
        const g = 0.88 + 0.12 * grain(u * 3, v * 0.5 + board);
        const l = gap ? 0.28 : shades[board]! * g;
        data.set([byte(0.6 * l), byte(0.52 * l), byte(0.44 * l), 255], (y * size + x) * 4);
      }
    }
  });
}

/**
 * A wood grain for the pupils' furniture (ADR-037): near white with darker streaks running along u,
 * one repeat a metre on a piece's box-projected UVs, multiplied by the light or dark wood's colour.
 */
export function grain(size = 512, seed = 47): THREE.DataTexture {
  const noise = fbm(seed, 4, 4);
  const fine = fbm(seed + 1, 3, 32);
  return drawn(size, size, (data) => {
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const u = x / size;
        const v = y / size;
        const rings = Math.sin((v * 22 + noise(u * 2, v * 4) * 5) * Math.PI);
        const l = 0.8 + 0.12 * rings + 0.08 * fine(u * 4, v * 32);
        data.set([byte(l), byte(l * 0.98), byte(l * 0.95), 255], (y * size + x) * 4);
      }
    }
  });
}

/**
 * The soft round shadow under a held piece (ADR-037): black, its alpha falling from the middle to
 * nothing at the edge, so a moving piece never needs the shadow map drawn again.
 */
export function blob(size = 128): THREE.DataTexture {
  return drawn(
    size,
    size,
    (data) => {
      const half = size / 2;
      for (let y = 0; y < size; y++) {
        for (let x = 0; x < size; x++) {
          const r = Math.hypot(x + 0.5 - half, y + 0.5 - half) / half;
          const a = r >= 1 ? 0 : 0.75 * (1 - r * r) ** 1.5;
          data.set([0, 0, 0, byte(a)], (y * size + x) * 4);
        }
      }
    },
    { srgb: false, repeat: false },
  );
}
